import { useEffect } from "react";
import { getUserProfileDataAsync } from "../store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { isBuilderPageAsync } from "../store/slices/builderIOSlice";

function getEncodedQueryString() {
  const params = new URLSearchParams(window.location.search);
  const encodedParams = new URLSearchParams();

  params.forEach((value, key) => {
    encodedParams.set(key, encodeURIComponent(value));
  });

  return (
    (encodedParams.toString() === "" ? "" : "?") + encodedParams.toString()
  );
}

function redirectBackwardCompatibleUrl(
  currentPathName: string,
  isAuthenticated: boolean,
  visitorSubdirectory: string,
  customerSubdirectory: string
) {
  let newPathName = null;
  let newQueryParameters = null;

  // Backward compatibility for Category URLs
  if (
    currentPathName === "/visitor/compatibility/AlgoliaSearch/SearchCategory" ||
    currentPathName ===
      "/lbpcustomer/compatibility/AlgoliaSearch/SearchCategory"
  ) {
    newPathName = `${
      isAuthenticated ? customerSubdirectory : visitorSubdirectory
    }/gallery`;
    newQueryParameters = window.location.search;
  }
  // Backward compatibility for Windows URLs
  else if (
    currentPathName === "/visitor/compatibility/AlgoliaSearch/SearchWindows" ||
    currentPathName === "/lbpcustomer/compatibility/AlgoliaSearch/SearchWindows"
  ) {
    newPathName = `${
      isAuthenticated ? customerSubdirectory : visitorSubdirectory
    }/gallery`;
    newQueryParameters = "?searchWindows=true";
  }
  // Backward compatibility for Renaissance URLs
  else if (
    currentPathName ===
      "/visitor/compatibility/AlgoliaSearch/SearchRenaissance" ||
    currentPathName ===
      "/lbpcustomer/compatibility/AlgoliaSearch/SearchRenaissance"
  ) {
    newPathName = `${
      isAuthenticated ? customerSubdirectory : visitorSubdirectory
    }/gallery`;
    newQueryParameters = "?searchRenaissance=true";
  }
  // Backward compatibility for Brand URLs
  else if (
    currentPathName === "/visitor/compatibility/AlgoliaSearch/SearchBrand" ||
    currentPathName === "/lbpcustomer/compatibility/AlgoliaSearch/SearchBrand"
  ) {
    newPathName = `${
      isAuthenticated ? customerSubdirectory : visitorSubdirectory
    }/gallery`;
    newQueryParameters = window.location.search;
  }

  if (newPathName !== null) {
    window.history.pushState({}, "", `${newPathName}${newQueryParameters}`);
    window.location.reload();
    return true;
  } else return false;
}

const extractAndRemoveQueryParam = (paramName: string): string | null => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(paramName);
  if (value !== null) {
    params.delete(paramName);
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  }
  return value;
};

const useAuthentication = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const encodedShareDataKey = extractAndRemoveQueryParam(
      "encodedShareDataKey"
    );
    dispatch(getUserProfileDataAsync(encodedShareDataKey));
    dispatch(isBuilderPageAsync());
  }, [dispatch]);

  const userData = useSelector((state: RootState) => state.userState);
  const user = userData.user; 
  const isBuilderPage = useSelector(
    (state: RootState) => state.builderIOState.isBuilderPage
  );

  //until we have one place for both visitor and authenticated user we should switch between those two urls
  useEffect(() => {
    if (!userData.isUserLoaded) return;
    if (isBuilderPage == null) return;

    // Preserve pathname and query parameters
    let pathName = window.location.pathname;
    let queryParameters = "";

    const customerPath = ""; //process.env.REACT_APP_STOREFRONT_SUBDIRECTORY!;
    const visitorPath = process.env.REACT_APP_SUBDIRECTORY!;
    const isVisitor = user.id === null || user.id === 0;
    const isOnVisitorPath = pathName.includes(visitorPath);

    const visitorSubdirectory = `/${process.env.REACT_APP_SUBDIRECTORY}`;
    const customerSubdirectory = `/${process.env.REACT_APP_STOREFRONT_SUBDIRECTORY}`;

    // If redirected to backward compatible URL, no need to go forward with redirection process
    if (
      redirectBackwardCompatibleUrl(
        pathName,
        userData.isAuthenticated,
        visitorSubdirectory,
        customerSubdirectory
      )
    )
      return;

    if (isVisitor && !isOnVisitorPath) {
      // Preserve pathname and query parameters if /lbpcustomer page is accessed, otherwise set to visitor homepage
      if (pathName.startsWith(customerSubdirectory)) {
        pathName = pathName.replace(customerSubdirectory, visitorSubdirectory);
        queryParameters = window.location.search;
      } else pathName = visitorSubdirectory;

      // In case of login with return URL, no redirection is needed
      if (
        window.location.pathname !== "/login" ||
        !window.location.search.startsWith("?ReturnUrl=")
      ) {
        window.history.pushState({}, "", `${pathName}${queryParameters}`);
        window.location.reload();
      }
    } else if (!isVisitor && isOnVisitorPath) {
      // Preserve pathname and query parameters if /visitor Builder.IO page is accessed, otherwise preserve deep link and redirect to MVC
      pathName = pathName.replace(visitorSubdirectory, customerSubdirectory);
      queryParameters = `${window.location.search}`;

      window.history.pushState({}, "", `${pathName}${queryParameters}`);
      window.location.reload();
    }
  }, [user.id, userData.isUserLoaded, isBuilderPage]);

  return {
    isAuthenticated: user.id !== null && user.id !== 0,
    isAuthenticating: !userData.isUserLoaded,
    user: user,
  };
};

export default useAuthentication;
